import type { AxiosError } from 'axios'
import client from './client.js'
import { parseAuthErrorCode, shouldInvalidateSession, type AuthErrorCode } from './authContract.ts'

type ApiErrorDetail = string | Record<string, unknown> | Array<unknown> | null

export type AuthApiError = {
  message: string
  status: number | null
  detail: ApiErrorDetail
  code: AuthErrorCode
  isSessionInvalidating?: boolean
}

export type AuthApiResult<T> =
  | { success: true; data: T }
  | { success: false; error: AuthApiError }

export type LoginResponse = {
  access_token: string
  token_type: 'bearer' | string
  must_change_password: boolean
  is_superadmin: boolean
}


export type PasswordResetRequestResponse = {
  message: string
}

export type ResetPasswordResponse = {
  success: boolean
  message: string
}

export type ChangePasswordResponse = {
  detail: string
}

export type AuthMeResponse = {
  id: number
  organization_id: number | null
  username: string
  email: string
  phone: string | null
  role: 'admin' | 'manager' | 'user' | string
  must_change_password: boolean
  is_superadmin: boolean
}

const toStructuredError = (error: unknown, fallbackMessage: string): AuthApiError => {
  const axiosError = error as AxiosError<{ detail?: ApiErrorDetail; message?: string }>
  const status = axiosError?.response?.status ?? null
  const detail = axiosError?.response?.data?.detail ?? null
  const messageFromBody = axiosError?.response?.data?.message

  let message = fallbackMessage
  if (typeof detail === 'string' && detail.trim()) {
    message = detail
  } else if (typeof messageFromBody === 'string' && messageFromBody.trim()) {
    message = messageFromBody
  } else if (axiosError?.message) {
    message = axiosError.message
  }

  const code = parseAuthErrorCode(status, message)
  const isSessionInvalidating = shouldInvalidateSession(status, code)

  return { message, status, detail, code, isSessionInvalidating }
}

export const login = async (
  username: string,
  password: string,
): Promise<AuthApiResult<LoginResponse>> => {
  try {
    const form = new URLSearchParams()
    form.append('username', username)
    form.append('password', password)

    const response = await client.post<LoginResponse>('/auth/login', form, {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    })

    return { success: true, data: response.data }
  } catch (error) {
    return {
      success: false,
      error: toStructuredError(error, 'Login failed.'),
    }
  }
}

export const requestPasswordReset = async (
  email: string,
): Promise<AuthApiResult<PasswordResetRequestResponse>> => {
  try {
    const response = await client.post<PasswordResetRequestResponse>('/auth/request-password-reset', {
      email,
    })

    return { success: true, data: response.data }
  } catch (error) {
    return {
      success: false,
      error: toStructuredError(error, 'Password reset request failed.'),
    }
  }
}

export const resetPassword = async (
  token: string,
  newPassword: string,
): Promise<AuthApiResult<ResetPasswordResponse>> => {
  try {
    const response = await client.post<ResetPasswordResponse>('/auth/reset-password', {
      token,
      new_password: newPassword,
    })

    return { success: true, data: response.data }
  } catch (error) {
    return {
      success: false,
      error: toStructuredError(error, 'Password reset failed.'),
    }
  }
}

export const changePassword = async (
  oldPassword: string,
  newPassword: string,
): Promise<AuthApiResult<ChangePasswordResponse>> => {
  try {
    const response = await client.post<ChangePasswordResponse>('/auth/change-password', {
      old_password: oldPassword,
      new_password: newPassword,
    })

    return { success: true, data: response.data }
  } catch (error) {
    return {
      success: false,
      error: toStructuredError(error, 'Change password failed.'),
    }
  }
}

export const getMe = async (): Promise<AuthApiResult<AuthMeResponse>> => {
  try {
    const response = await client.get<AuthMeResponse>('/auth/me')
    return { success: true, data: response.data }
  } catch (error) {
    return {
      success: false,
      error: toStructuredError(error, 'Failed to load user profile.'),
    }
  }
}
